import React, {useState, useEffect} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    IconButton,
} from "@mui/material";
import {ICalendarEvent} from "../../interfaces/ICalendarEvent";
import {IProfile} from "../../interfaces/IUser";
import {Box} from "@mui/system";
import DeleteIcon from "@mui/icons-material/Delete";
import {ISubCalendar} from "../../interfaces/ISubCalendar";
import CheckIcon from "@mui/icons-material/Check";
import {ListItemIcon, ListItemText, FormHelperText} from "@mui/material";


// Hilfsfunktion: Konvertiert einen Datums-String in das von datetime-local erwartete Format.
const formatForDatetimeLocal = (dateString: string): string => {
    if (dateString.includes("Z")) {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return dateString.replace(" ", "T").slice(0, 16);
};

interface CalendarEventEditProps {
    open: boolean;
    event: ICalendarEvent | null;
    onClose: () => void;
    onSave: (updatedEvent: ICalendarEvent) => void;
    userArray: IProfile[];
    onDelete: () => void;
    subCalendarArray: ISubCalendar[];
    initialSubCalendars?: string[];
}

const CalendarEventEdit: React.FC<CalendarEventEditProps> = ({
                                                                 open,
                                                                 event,
                                                                 onClose,
                                                                 onSave,
                                                                 userArray,
                                                                 onDelete,
                                                                 subCalendarArray,
                                                                 initialSubCalendars
                                                             }) => {
    const [title, setTitle] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [description, setDescription] = useState("");
    const [who, setWho] = useState("");
    const [where, setWhere] = useState("");
    const [wholeDay, setWholeDay] = useState(false);
    // Hier speichern wir die ausgewählten Kalender als string[] (ID als String)
    const [selectedSubCalendars, setSelectedSubCalendars] = useState<string[]>([]);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // Error states für Pflichtfelder
    const [titleError, setTitleError] = useState(false);
    const [startError, setStartError] = useState(false);
    const [endError, setEndError] = useState(false);
    const [subCalError, setSubCalError] = useState(false);

    const [subCalSelectOpen, setSubCalSelectOpen] = useState(false);

    useEffect(() => {
        if (event) {
            // Felder aus dem Event setzen
            setTitle(event.title);
            setStart(event.start ? formatForDatetimeLocal(event.start) : "");
            setEnd(event.end ? formatForDatetimeLocal(event.end) : "");
            setDescription(event.extendedProps?.description || "");
            setWho(event.extendedProps?.who || "");
            setWhere(event.extendedProps?.where || "");
            setWholeDay(event.extendedProps?.wholeDay || false);

            // SubCalendars für neuen Termin vorbelegen, sonst aus extendedProps mappen
            if (event.id === "" && initialSubCalendars && initialSubCalendars.length > 0) {
                setSelectedSubCalendars(initialSubCalendars);
            } else if (event.extendedProps?.subCalendars && subCalendarArray) {
                const selectedIds = event.extendedProps.subCalendars
                    .map((name) => {
                        const found = subCalendarArray.find((sc) => sc.name === name);
                        return found ? found.id.toString() : "";
                    })
                    .filter((id) => id !== "");
                setSelectedSubCalendars(selectedIds);
            } else {
                setSelectedSubCalendars([]);
            }

            // Fehler-Flags zurücksetzen
            setTitleError(false);
            setStartError(false);
            setEndError(false);
            setSubCalError(false);
        } else {
            // Wenn event === null (Dialog geschlossen) alles zurücksetzen
            setTitle("");
            setStart("");
            setEnd("");
            setDescription("");
            setWho("");
            setWhere("");
            setWholeDay(false);
            setSelectedSubCalendars([]);
            setTitleError(false);
            setStartError(false);
            setEndError(false);
            setSubCalError(false);
        }
    }, [event, subCalendarArray, initialSubCalendars]);


    const handleSave = () => {
        let valid = true;
        if (selectedSubCalendars.length === 0) {
            setSubCalError(true);
            valid = false;
        } else {
            setSubCalError(false);
        }
        if (!title.trim()) {
            setTitleError(true);
            valid = false;
        }
        if (!start) {
            setStartError(true);
            valid = false;
        }
        if (!end) {
            setEndError(true);
            valid = false;
        }
        if (!valid) return;
        if (event) {
            const updatedEvent: ICalendarEvent = {
                ...event,
                title,
                start,
                end,
                extendedProps: {
                    ...event.extendedProps,
                    description,
                    who,
                    where,
                    wholeDay,
                    // Übergeben als string[] (z.B. ["Geschäftsführer", "Dennis Garte"])
                    subCalendars: selectedSubCalendars,
                },
            };
            onSave(updatedEvent);
        }
    };

    return (
        <>
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}>
                <DialogTitle>Termin löschen</DialogTitle>
                <DialogContent>Möchten Sie diesen Termin wirklich löschen?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)} color="secondary">
                        Abbrechen
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmDeleteOpen(false);
                            onDelete();
                        }}
                        color="error"
                        variant="contained"
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <span>{event?.id != "" ? "Termin bearbeiten" : "Neuen Termin erstellen"}</span>
                        <IconButton onClick={() => setConfirmDeleteOpen(true)} color="error">
                            <DeleteIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Titel"
                        fullWidth
                        margin="normal"
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                            if (e.target.value.trim()) setTitleError(false);
                        }}
                        required
                        error={titleError}
                        helperText={titleError ? "Titel ist erforderlich" : ""}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Start"
                                type="datetime-local"
                                fullWidth
                                margin="normal"
                                value={start}
                                onChange={(e) => {
                                    const newStart = e.target.value;
                                    setStart(newStart);
                                    if (newStart) setStartError(false);

                                    const startDate = new Date(newStart);
                                    const endDate = end ? new Date(end) : null;
                                    if (!endDate || startDate >= endDate) {
                                        const newEndDate = new Date(startDate.getTime() + 60 * 60 * 1000);
                                        setEnd(formatForDatetimeLocal(newEndDate.toISOString()));
                                    }
                                }}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Ende"
                                type="datetime-local"
                                fullWidth
                                margin="normal"
                                value={end}
                                onChange={(e) => {
                                    const newEnd = e.target.value;
                                    setEnd(newEnd);
                                    if (newEnd) setEndError(false);

                                    if (start) {
                                        const endDate = new Date(newEnd);
                                        const startDate = new Date(start);
                                        if (endDate <= startDate) {
                                            const newStartDate = new Date(endDate.getTime() - 60 * 60 * 1000);
                                            setStart(formatForDatetimeLocal(newStartDate.toISOString()));
                                        }
                                    }
                                }}
                                InputLabelProps={{shrink: true}}
                                required
                                error={endError}
                                helperText={endError ? "Endzeit ist erforderlich" : ""}
                            />
                        </Grid>
                    </Grid>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={wholeDay}
                                onChange={(e) => setWholeDay(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Ganztägiges Event"
                    />
                    <TextField
                        label="Beschreibung"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <TextField
                        label="Wer"
                        fullWidth
                        margin="normal"
                        value={who}
                        onChange={(e) => setWho(e.target.value)}
                    />
                    <TextField
                        label="Wo"
                        fullWidth
                        margin="normal"
                        value={where}
                        onChange={(e) => setWhere(e.target.value)}
                    />
                    {/* Dropdown für Mehrfachauswahl der Kalender */}
                    <FormControl fullWidth margin="normal" required error={subCalError}>
                        <InputLabel id="subcalendar-select-label">Kalender zuweisen</InputLabel>
                        <Select
                            labelId="subcalendar-select-label"
                            multiple
                            open={subCalSelectOpen}
                            onOpen={() => setSubCalSelectOpen(true)}
                            onClose={() => setSubCalSelectOpen(false)}
                            value={selectedSubCalendars}
                            onChange={(e) => {
                                const vals = e.target.value as string[];
                                setSelectedSubCalendars(vals);
                                setSubCalSelectOpen(false);
                                if (subCalError) {
                                    setSubCalError(false);
                                }
                            }}
                            input={<OutlinedInput label="Kalender zuweisen"/>}
                            renderValue={(selected) => {
                                const selectedIds = selected as string[];
                                const selectedNames = subCalendarArray
                                    .filter((sc) => selectedIds.includes(sc.id.toString()))
                                    .map((sc) => sc.name);
                                return selectedNames.join(", ");
                            }}
                        >
                            {subCalendarArray.map((sc) => {
                                const isSelected = selectedSubCalendars.includes(sc.id.toString());
                                return (
                                    <MenuItem
                                        key={sc.id}
                                        value={sc.id.toString()}
                                        selected={isSelected}
                                        sx={{
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(76, 175, 80, 0.2)",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 32,
                                                color: isSelected ? "success.main" : "transparent",
                                            }}
                                        >
                                            <CheckIcon fontSize="small"/>
                                        </ListItemIcon>
                                        <ListItemText primary={sc.name}/>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {subCalError && (
                            <FormHelperText>Mindestens ein Kalender muss ausgewählt sein</FormHelperText>
                        )}
                    </FormControl>


                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="secondary">
                        Abbrechen
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained">
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CalendarEventEdit;
